/**
 * React Flow edge interactions that mirror state into Loro: new connections,
 * bulk/single delete, and style updates (including persisting merged edge data).
 *
 * Expects `lastEdgeStyle` from `useCanvasShapeAndEdgeStyle` so new edges pick up
 * the current toolbar style. All handlers no-op when the user lacks `can_edit`
 * (see `useCanvasPermissions`).
 */

import {
  DEFAULT_EDGE_STYLE,
  type LastEdgeStyle,
} from "@/pages/canvas/hooks/useCanvasShapeAndEdgeStyle";
import type { EdgeStyle } from "@/pages/canvas/nodes/LineFloatingToolbar";
import {
  applyEdgeStyle,
  readEdgeStyle,
} from "@/pages/canvas/utils/applyEdgeStyle";
import { trackCanvasEdgeConnected } from "@/utils/analytics";
import type { Connection, Edge, Node } from "@xyflow/react";
import { useReactFlow } from "@xyflow/react";
import { useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import { useCanvas } from "../context/CanvasContext";
import type { LabeledEdgeData } from "../types/canvasNodeData";
import { getFloatingEdgeParams, sideFromData } from "../utils/floatingEdge";
import { settledRouteNow, type LineGeometry } from "../utils/linePath";
import { nodeStackingZ } from "../utils/nodeStacking";
import { useCanvasPermissions } from "./useCanvasPermissions";

/** Matches ahooks `useSessionStorageState` setter (`createUseStorageState.SetState`). */
type AhooksStorageSetter<T> = (value: T | ((prevState?: T) => T)) => void;

export interface UseCanvasEdgeHandlersParams {
  setLastEdgeStyle: AhooksStorageSetter<LastEdgeStyle>;
  lastEdgeStyle: LastEdgeStyle;
}

export function useCanvasEdgeHandlers({
  setLastEdgeStyle,
  lastEdgeStyle,
}: UseCanvasEdgeHandlersParams) {
  const reactFlow = useReactFlow<Node, Edge>();
  const { setEdges } = reactFlow;
  const { canEdit } = useCanvasPermissions();
  const { addEdge, removeEdge, removeEdges, updateEdge } = useCanvas();
  const handleConnect = useCallback(
    (connection: Connection) => {
      if (!canEdit) return;
      // Translate the chosen style into native RF fields; `data` keeps only
      // curve_type. Native fields are what renders + persists.
      const styled = applyEdgeStyle({
        ...connection,
        id: uuidv4(),
        data: lastEdgeStyle as Record<string, unknown>,
      });
      const edge: Edge = {
        ...styled,
        data: { curve_type: lastEdgeStyle.curve_type },
      };
      trackCanvasEdgeConnected({ edgeType: edge.type });
      setEdges((eds) => [...eds, edge]);
      addEdge({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        type: edge.type,
        sourceHandle: edge.sourceHandle ?? undefined,
        targetHandle: edge.targetHandle ?? undefined,
        style: edge.style,
        markerStart: edge.markerStart,
        markerEnd: edge.markerEnd,
        data: edge.data as Record<string, unknown>,
      });
    },
    [canEdit, setEdges, addEdge, lastEdgeStyle],
  );

  const handleEdgesDelete = useCallback(
    (deletedEdges: Edge[]) => {
      if (!canEdit || deletedEdges.length === 0) return;
      removeEdges(deletedEdges.map((e) => e.id));
    },
    [canEdit, removeEdges],
  );

  const handleEdgeStyleChange = useCallback(
    (edgeId: string, style: EdgeStyle) => {
      if (!canEdit) return;
      setLastEdgeStyle((prev) => ({
        ...(prev ?? DEFAULT_EDGE_STYLE),
        ...style,
      }));
      setEdges((eds) => {
        return eds.map((e) => {
          if (e.id !== edgeId) return e;
          // Current semantic style comes from the edge's native fields; merge
          // the toolbar's change and re-apply to native fields.
          const current = readEdgeStyle(e);
          const merged: EdgeStyle = { ...current, ...style };
          const styled = applyEdgeStyle({
            ...e,
            data: merged as Record<string, unknown>,
          });
          // Everything else in data (label style, route) carries through; a
          // change of curve drops the route, which was drawn for the old
          // curve. Re-picking the current curve is not a change.
          const curveChanged = merged.curve_type !== current.curve_type;
          const {
            bend: _bend,
            points: _points,
            ...carried
          } = (e.data ?? {}) as Record<string, unknown>;
          const edge: Edge = {
            ...styled,
            data: {
              ...(curveChanged ? carried : (e.data ?? {})),
              curve_type: merged.curve_type,
            },
          };
          addEdge({
            id: edgeId,
            source: edge.source,
            target: edge.target,
            type: edge.type,
            sourceHandle: edge.sourceHandle ?? undefined,
            targetHandle: edge.targetHandle ?? undefined,
            style: edge.style,
            markerStart: edge.markerStart,
            markerEnd: edge.markerEnd,
            data: edge.data as Record<string, unknown>,
            // `add` is a full edge replace in Liveblocks storage (LiveObject
            // reconcile in "full" mode deletes keys absent from the payload),
            // so carry the existing label through — `e` is the original edge
            // (applyEdgeStyle's `edge` doesn't include `label`). Without this,
            // a style change would wipe a label set earlier.
            label: e.label,
            selected: e.selected,
            zIndex: e.zIndex,
          });
          return edge;
        });
      });
    },
    [canEdit, setEdges, addEdge, setLastEdgeStyle],
  );

  const handleEdgeRouteAuto = useCallback(
    (edgeId: string) => {
      if (!canEdit) return;
      updateEdge(edgeId, {
        source_side: null,
        target_side: null,
        bend: null,
        points: null,
        points_frame: null,
      });
    },
    [canEdit, updateEdge],
  );

  // Edges paint just above the nodes by default. Bring to front lifts the
  // edge above a node that was itself brought to front; send to back drops
  // it under every node.
  const handleEdgeBringToFront = useCallback(
    (edgeId: string) => {
      if (!canEdit) return;
      const maxZ = Math.max(0, ...reactFlow.getNodes().map(nodeStackingZ));
      updateEdge(edgeId, { zIndex: maxZ + 1 });
    },
    [canEdit, reactFlow, updateEdge],
  );

  const handleEdgeSendToBack = useCallback(
    (edgeId: string) => {
      if (!canEdit) return;
      updateEdge(edgeId, { zIndex: 0 });
    },
    [canEdit, updateEdge],
  );

  const handleEdgeDelete = useCallback(
    (edgeId: string) => {
      if (!canEdit) return;
      removeEdge(edgeId);
      setEdges((eds) => eds.filter((e) => e.id !== edgeId));
    },
    [canEdit, removeEdge, setEdges],
  );

  // After nodes moved, store every hand-drawn route as it stands now with a
  // fresh record of where its ends are, so the next joint move translates
  // it exactly and the next single move adjusts only the segments nearest
  // the node that moved. Routes whose ends did not move are left alone.
  const handleNodesMoved = useCallback(() => {
    if (!canEdit) return;
    for (const e of reactFlow.getEdges()) {
      const d = (e.data ?? {}) as LabeledEdgeData;
      if (!d.points || d.points.length === 0) continue;
      const floating = getFloatingEdgeParams(
        reactFlow.getInternalNode(e.source),
        reactFlow.getInternalNode(e.target),
        {
          sourceSide: sideFromData(d.source_side),
          targetSide: sideFromData(d.target_side),
        },
      );
      if (!floating) continue;
      const geometry: LineGeometry = {
        curve: "step",
        source: { x: floating.sx, y: floating.sy, side: floating.sourcePos },
        target: { x: floating.tx, y: floating.ty, side: floating.targetPos },
        points: d.points,
        pointsFrame: d.points_frame ?? null,
      };
      const route = settledRouteNow(geometry);
      if (!route) continue;
      const was = d.points_frame;
      const same = (a: { x: number; y: number }, b: { x: number; y: number }) =>
        a.x === b.x && a.y === b.y;
      if (
        was &&
        same(was.source, route.points_frame.source) &&
        same(was.target, route.points_frame.target)
      ) {
        continue;
      }
      updateEdge(e.id, route);
    }
  }, [canEdit, reactFlow, updateEdge]);

  return {
    handleConnect,
    handleEdgesDelete,
    handleNodesMoved,
    handleEdgeStyleChange,
    handleEdgeRouteAuto,
    handleEdgeBringToFront,
    handleEdgeSendToBack,
    handleEdgeDelete,
  };
}
